import type { TacticAction } from "../types/actions";
import type { PlayerId } from "../types/common";
import type { WarchestState } from "../types/game-state";
import { validateTactic } from "../validators/validate-tactic";

interface FootmanManeuver {
  readonly footmanHex: string;
  readonly action: "MOVE" | "ATTACK" | "CONTROL";
  readonly targetHex: string;
}

export interface TacticCandidate {
  readonly action: TacticAction;
  readonly primaryHex: string;
  readonly involvedHexes: readonly string[];
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function createCandidateCollector(state: WarchestState) {
  const seen = new Set<string>();
  const candidates: TacticCandidate[] = [];

  return {
    add(
      action: TacticAction,
      primaryHex: string,
      involvedHexes: readonly string[],
    ): void {
      if (!validateTactic(state, action).valid) return;
      const key = JSON.stringify(action);
      if (seen.has(key)) return;
      seen.add(key);
      candidates.push({
        action,
        primaryHex,
        involvedHexes: unique([action.unitHex, ...involvedHexes]),
      });
    },
    values(): readonly TacticCandidate[] {
      return candidates;
    },
  };
}

function listUnitHexes(
  state: WarchestState,
  player: PlayerId,
  unitType: string,
): string[] {
  const boardDef = state.config.boards[state.boardId];
  if (!boardDef) return [];
  return boardDef.hexes.filter((hex) => {
    const cell = state.board[hex];
    return cell?.owner === player && cell.unitType === unitType;
  });
}

function listRoyalGuardHexes(state: WarchestState, player: PlayerId): string[] {
  return listUnitHexes(state, player, "ROYAL_GUARD");
}

function enumerateEmptyPaths(
  state: WarchestState,
  startHex: string,
  maxLength: number,
): readonly string[][] {
  const boardDef = state.config.boards[state.boardId];
  if (!boardDef || maxLength <= 0) return [];
  const paths: string[][] = [];

  function walk(currentHex: string, path: readonly string[]): void {
    if (path.length > 0) paths.push([...path]);
    if (path.length >= maxLength) return;
    for (const nextHex of boardDef.adjacency[currentHex] ?? []) {
      if (path.includes(nextHex)) continue;
      if (state.board[nextHex]) continue;
      walk(nextHex, [...path, nextHex]);
    }
  }

  walk(startHex, []);
  return paths;
}

function listFootmanManeuvers(
  state: WarchestState,
  player: PlayerId,
): readonly FootmanManeuver[] {
  const boardDef = state.config.boards[state.boardId];
  if (!boardDef) return [];
  const maneuvers: FootmanManeuver[] = [];
  for (const footmanHex of listUnitHexes(state, player, "FOOTMAN")) {
    for (const targetHex of boardDef.adjacency[footmanHex] ?? []) {
      const target = state.board[targetHex];
      if (!target) {
        maneuvers.push({ footmanHex, action: "MOVE", targetHex });
      } else if (target.owner !== player) {
        maneuvers.push({ footmanHex, action: "ATTACK", targetHex });
      }
    }
  }
  return maneuvers;
}

function addFootmanCandidates(
  state: WarchestState,
  player: PlayerId,
  coinType: string,
  unitHexes: readonly string[],
  collector: ReturnType<typeof createCandidateCollector>,
): void {
  const unitHex = unitHexes[0];
  if (!unitHex) return;
  const maneuvers = listFootmanManeuvers(state, player);
  for (const maneuver of maneuvers) {
    collector.add(
      {
        type: "TACTIC",
        player,
        coinType,
        unitHex,
        tacticPayload: { maneuvers: [maneuver] },
      },
      maneuver.targetHex,
      [maneuver.footmanHex, maneuver.targetHex],
    );
  }

  for (let i = 0; i < maneuvers.length; i += 1) {
    for (let j = i + 1; j < maneuvers.length; j += 1) {
      const first = maneuvers[i];
      const second = maneuvers[j];
      if (!first || !second) continue;
      if (first.footmanHex === second.footmanHex) continue;
      collector.add(
        {
          type: "TACTIC",
          player,
          coinType,
          unitHex,
          tacticPayload: { maneuvers: [first, second] },
        },
        second.targetHex,
        [
          first.footmanHex,
          first.targetHex,
          second.footmanHex,
          second.targetHex,
        ],
      );
    }
  }
}

export function listTacticCandidates(
  state: WarchestState,
  player: PlayerId,
  coinType: string,
): readonly TacticCandidate[] {
  const boardDef = state.config.boards[state.boardId];
  if (!boardDef) return [];
  const collector = createCandidateCollector(state);

  const unitDef = state.config.units[coinType];
  const tacticId =
    coinType === "ROYAL" ? "ROYAL_GUARD_PROTECT" : unitDef?.tacticId;
  if (!tacticId) return [];

  const unitHexes =
    coinType === "ROYAL"
      ? listRoyalGuardHexes(state, player)
      : listUnitHexes(state, player, coinType);

  for (const unitHex of unitHexes) {
    switch (tacticId) {
      case "ARCHER_RANGED":
      case "CROSSBOWMAN_BOLT":
        for (const targetHex of boardDef.hexes) {
          collector.add(
            {
              type: "TACTIC",
              player,
              coinType,
              unitHex,
              tacticPayload: { targetHex },
            },
            targetHex,
            [targetHex],
          );
        }
        break;

      case "BERSERKER_CHARGE": {
        const cell = state.board[unitHex];
        const maxLength = cell ? cell.stackSize + 1 : 1;
        for (const moves of enumerateEmptyPaths(state, unitHex, maxLength)) {
          const primaryHex = moves[moves.length - 1];
          if (!primaryHex) continue;
          collector.add(
            {
              type: "TACTIC",
              player,
              coinType,
              unitHex,
              tacticPayload: { moves },
            },
            primaryHex,
            moves,
          );
        }
        break;
      }

      case "CAVALRY_MANEUVER":
        for (const toHex of boardDef.adjacency[unitHex] ?? []) {
          for (const attackHex of boardDef.adjacency[toHex] ?? []) {
            collector.add(
              {
                type: "TACTIC",
                player,
                coinType,
                unitHex,
                tacticPayload: { toHex, attackHex },
              },
              attackHex,
              [toHex, attackHex],
            );
          }
        }
        break;

      case "ENSIGN_RALLY":
        for (const friendlyHex of boardDef.hexes) {
          for (const toHex of boardDef.adjacency[friendlyHex] ?? []) {
            collector.add(
              {
                type: "TACTIC",
                player,
                coinType,
                unitHex,
                tacticPayload: { friendlyHex, toHex },
              },
              toHex,
              [friendlyHex, toHex],
            );
          }
        }
        break;

      case "FOOTMAN_FORMATION":
        addFootmanCandidates(state, player, coinType, unitHexes, collector);
        break;

      case "LANCER_THRUST":
        for (const step1 of boardDef.adjacency[unitHex] ?? []) {
          for (const attackHex of boardDef.hexes) {
            collector.add(
              {
                type: "TACTIC",
                player,
                coinType,
                unitHex,
                tacticPayload: { path: [step1], attackHex },
              },
              attackHex,
              [step1, attackHex],
            );
            for (const step2 of boardDef.adjacency[step1] ?? []) {
              collector.add(
                {
                  type: "TACTIC",
                  player,
                  coinType,
                  unitHex,
                  tacticPayload: { path: [step1, step2], attackHex },
                },
                attackHex,
                [step1, step2, attackHex],
              );
            }
          }
        }
        break;

      case "LIGHT_CAVALRY_SWIFT":
        for (const step1 of boardDef.adjacency[unitHex] ?? []) {
          for (const step2 of boardDef.adjacency[step1] ?? []) {
            collector.add(
              {
                type: "TACTIC",
                player,
                coinType,
                unitHex,
                tacticPayload: { path: [step1, step2] },
              },
              step2,
              [step1, step2],
            );
          }
        }
        break;

      case "MARSHALL_COMMAND":
        for (const friendlyHex of boardDef.adjacency[unitHex] ?? []) {
          for (const targetHex of boardDef.adjacency[friendlyHex] ?? []) {
            collector.add(
              {
                type: "TACTIC",
                player,
                coinType,
                unitHex,
                tacticPayload: { friendlyHex, targetHex },
              },
              targetHex,
              [friendlyHex, targetHex],
            );
          }
        }
        break;

      case "ROYAL_GUARD_PROTECT":
        for (const toHex of boardDef.adjacency[unitHex] ?? []) {
          collector.add(
            {
              type: "TACTIC",
              player,
              coinType,
              unitHex,
              tacticPayload: { toHex },
            },
            toHex,
            [toHex],
          );
        }
        break;
    }
  }

  return collector.values();
}

export function listTacticTargetHexes(
  state: WarchestState,
  player: PlayerId,
  coinType: string,
): readonly string[] {
  return unique(
    listTacticCandidates(state, player, coinType).map(
      (candidate) => candidate.primaryHex,
    ),
  );
}
